import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  Menu,
  ChevronDown,
  LogOut,
  User,
  Shield,
  Check,
} from "lucide-react";
import { STORAGE_KEY } from "../../../features/api/apiSlice";
import { useMeQuery } from "../../../modules/auth/api/authApi";
import { useAuth } from "../../../context/AuthContexts";

function getAuth(): any {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function pageTitleFromPath(pathname: string) {
  if (pathname === "/" || pathname === "/dashboard") return "Dashboard";
  const seg = pathname.split("/").filter(Boolean).pop() || "Dashboard";
  return seg.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

function prettyRole(role?: string | null) {
  if (!role) return "Member";
  return role
    .split(/[-_]/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function resolveProfilePic(pro_pic?: string | null) {
  if (!pro_pic) return null;
  if (pro_pic.startsWith("http")) return pro_pic;
  const base = "https://api.niqsng.org";
  const cleanPath = pro_pic.startsWith("/") ? pro_pic : `/${pro_pic}`;
  return `${base}/storage${cleanPath}`;
}

export default function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const location = useLocation();
  const navigate = useNavigate();
  const title = pageTitleFromPath(location.pathname);

  const _auth = useMemo(() => getAuth(), []);
  const { data, isLoading: _isLoading, isError: _isError } = useMeQuery();
  const storedKey = "logbook_auth_user_v2";
  const raw = localStorage.getItem(storedKey);
  const user = data?.user || (raw ? JSON.parse(raw) : null);

  const { activeRoleName, setActiveRole, user: authUser } = useAuth();

  // Collect all roles from auth context (supports both string[] and {name, permissions}[])
  const allRoles: string[] = useMemo(() => {
    const roles = authUser?.roles || [];
    return roles
      .map((r) => (typeof r === "string" ? r : r?.name))
      .filter(Boolean) as string[];
  }, [authUser]);

  const currentRole = activeRoleName || allRoles[0] || null;

  // A practice's own login rather than a person's. /me first, localStorage
  // second — the same order as `user` above.
  const isFirmAccount =
    String(data?.user?.whois ?? user?.whois ?? "").toLowerCase() === "firm";

  const [openProfile, setOpenProfile] = useState(false);
  const [openRoles, setOpenRoles] = useState(false);

  const profileSrc = resolveProfilePic(user?.user?.pro_pic || user?.pro_pic);

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem(storedKey);
    window.location.href = "/login";
  };

  const goToProfile = () => {
    setOpenProfile(false);
    navigate("/profile");
  };

  const handleRoleSwitch = (role: string) => {
    setActiveRole(role);
    setOpenRoles(false);
  };

  return (
    <header className="bg-white border-b sticky top-0 z-40">
      <div className="h-16 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded hover:bg-gray-100"
          >
            <Menu className="w-5 h-5" />
          </button>

          <h1 className="font-semibold text-gray-800">{title}</h1>
        </div>

        <div className="flex items-center gap-2">
          <button className="p-2 rounded hover:bg-gray-100">
            <Bell className="w-5 h-5" />
          </button>

          {/* Role switcher — only shown when user has roles */}
          {allRoles.length > 0 && (
            <div className="relative">
              <button
                onClick={() => {
                  setOpenRoles((v) => !v);
                  setOpenProfile(false);
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
              >
                <Shield className="w-3.5 h-3.5 text-blue-600" />
                <span className="text-xs font-medium text-gray-700 max-w-[90px] truncate">
                  {prettyRole(currentRole)}
                </span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </button>

              {openRoles && (
                <div className="absolute right-0 mt-2 w-48 bg-white border rounded-lg shadow-lg py-1 z-50">
                  <p className="px-3 py-1.5 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                    Switch Role
                  </p>
                  {allRoles.map((role) => (
                    <button
                      key={role}
                      onClick={() => handleRoleSwitch(role)}
                      className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${
                        currentRole === role
                          ? "text-blue-600 font-semibold"
                          : "text-gray-700"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Shield
                          className={`w-3.5 h-3.5 ${
                            currentRole === role
                              ? "text-blue-500"
                              : "text-gray-400"
                          }`}
                        />
                        {prettyRole(role)}
                      </span>
                      {currentRole === role && (
                        <Check className="w-3.5 h-3.5 text-blue-500" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Profile dropdown */}
          <div className="relative">
            <button
              onClick={() => {
                setOpenProfile((v) => !v);
                setOpenRoles(false);
              }}
              className="flex items-center gap-2 p-1 rounded hover:bg-gray-100"
            >
              <div className="h-8 w-8 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-xs font-bold">
                {profileSrc ? (
                  <img
                    src={profileSrc}
                    alt="User"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  (user?.name || "U").slice(0, 1)
                )}
              </div>

              <ChevronDown className="w-4 h-4" />
            </button>

            {openProfile && (
              <div className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-50">
                <div
                  onClick={goToProfile}
                  className="px-4 py-3 border-b cursor-pointer hover:bg-gray-50"
                >
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>

                {/* A FIRM's account has no personal profile to edit — the
                    profile page asks a member's questions (grade, chapter,
                    date of birth), and the guard sends a firm straight back
                    from it. Offering the link would only lead nowhere. */}
                {!isFirmAccount && (
                  <button
                    onClick={goToProfile}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                  >
                    <User className="w-4 h-4" />
                    Edit Profile
                  </button>
                )}

                <button
                  onClick={logout}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
